import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { 
  Accessibility, 
  X, 
  ZoomIn, 
  ZoomOut, 
  Type, 
  Contrast,
  MousePointer2,
  Underline,
  Eye,
  RotateCcw
} from 'lucide-react';

const A11Y_KEY = 'wheel_a11y_settings';

const defaultSettings = {
  fontSize: 100,
  contrast: 'normal', // normal, high, inverted
  cursorSize: 'normal', // normal, large
  underlineLinks: false,
  highlightFocus: false,
  reducedMotion: false
};

const AccessibilityWidget = () => {
  const { t, isRTL } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [settings, setSettings] = useState(defaultSettings);

  // Load settings on mount
  useEffect(() => {
    const saved = localStorage.getItem(A11Y_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setSettings({ ...defaultSettings, ...parsed });
      } catch {
        // Use defaults
      }
    }
  }, []);

  // Apply settings to document
  useEffect(() => {
    const root = document.documentElement;
    
    // Font size
    root.style.fontSize = `${settings.fontSize}%`;
    
    // Contrast modes
    root.classList.remove('high-contrast', 'inverted-contrast');
    if (settings.contrast === 'high') {
      root.classList.add('high-contrast');
    } else if (settings.contrast === 'inverted') {
      root.classList.add('inverted-contrast');
    }
    
    // Cursor size
    root.classList.toggle('large-cursor', settings.cursorSize === 'large');
    
    // Underline links
    root.classList.toggle('underline-links', settings.underlineLinks);
    
    // Highlight focus
    root.classList.toggle('highlight-focus', settings.highlightFocus);
    
    // Reduced motion
    root.classList.toggle('reduced-motion', settings.reducedMotion);
    
    // Save to localStorage
    localStorage.setItem(A11Y_KEY, JSON.stringify(settings));
  }, [settings]);

  const updateSetting = useCallback((key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  }, []);

  const increaseFontSize = () => {
    setSettings(prev => ({ 
      ...prev, 
      fontSize: Math.min(prev.fontSize + 10, 150) 
    }));
  };

  const decreaseFontSize = () => {
    setSettings(prev => ({ 
      ...prev, 
      fontSize: Math.max(prev.fontSize - 10, 80) 
    }));
  };

  const cycleContrast = () => {
    setSettings(prev => {
      const modes = ['normal', 'high', 'inverted'];
      const currentIndex = modes.indexOf(prev.contrast);
      const nextIndex = (currentIndex + 1) % modes.length;
      return { ...prev, contrast: modes[nextIndex] };
    });
  };

  const resetSettings = () => {
    setSettings(defaultSettings);
  };

  const getContrastLabel = () => {
    switch (settings.contrast) {
      case 'high': return 'High Contrast';
      case 'inverted': return 'Inverted';
      default: return 'Normal';
    }
  };

  return (
    <>
      {/* Accessibility Toggle Button (bottom-right) */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`a11y-toggle-btn ${isOpen ? 'active' : ''}`}
        aria-label="Accessibility options"
        aria-expanded={isOpen}
        title="Accessibility Options"
        data-testid="a11y-toggle"
      >
        <Accessibility size={24} />
      </button>

      {/* Accessibility Panel */}
      {isOpen && (
        <div 
          className={`a11y-panel ${isRTL ? 'rtl' : ''}`}
          role="dialog"
          aria-label="Accessibility settings"
          data-testid="a11y-panel"
        >
          <div className="a11y-header">
            <h3>
              <Accessibility size={20} />
              Accessibility
            </h3>
            <button 
              onClick={() => setIsOpen(false)}
              className="a11y-close"
              aria-label="Close accessibility panel"
            >
              <X size={20} />
            </button>
          </div>

          <div className="a11y-content">
            {/* Font Size */}
            <div className="a11y-section">
              <label className="a11y-label">
                <Type size={18} />
                Text Size ({settings.fontSize}%)
              </label>
              <div className="a11y-controls">
                <button 
                  onClick={decreaseFontSize}
                  disabled={settings.fontSize <= 80}
                  aria-label="Decrease text size"
                  className="a11y-btn"
                >
                  <ZoomOut size={18} />
                </button>
                <div className="a11y-bar">
                  <div 
                    className="a11y-bar-fill"
                    style={{ width: `${((settings.fontSize - 80) / 70) * 100}%` }}
                  />
                </div>
                <button 
                  onClick={increaseFontSize}
                  disabled={settings.fontSize >= 150}
                  aria-label="Increase text size"
                  className="a11y-btn"
                >
                  <ZoomIn size={18} />
                </button>
              </div>
            </div>

            {/* Contrast */}
            <div className="a11y-section">
              <label className="a11y-label">
                <Contrast size={18} />
                Contrast: {getContrastLabel()}
              </label>
              <button 
                onClick={cycleContrast}
                className="a11y-btn-wide"
              >
                Change Contrast
              </button>
            </div>

            {/* Cursor Size */}
            <div className="a11y-section">
              <label className="a11y-label">
                <MousePointer2 size={18} />
                Large Cursor
              </label>
              <button
                onClick={() => updateSetting('cursorSize', settings.cursorSize === 'large' ? 'normal' : 'large')}
                className={`a11y-toggle ${settings.cursorSize === 'large' ? 'active' : ''}`}
                aria-pressed={settings.cursorSize === 'large'}
              >
                {settings.cursorSize === 'large' ? 'ON' : 'OFF'}
              </button>
            </div>

            {/* Underline Links */}
            <div className="a11y-section">
              <label className="a11y-label">
                <Underline size={18} />
                Underline Links
              </label>
              <button
                onClick={() => updateSetting('underlineLinks', !settings.underlineLinks)}
                className={`a11y-toggle ${settings.underlineLinks ? 'active' : ''}`}
                aria-pressed={settings.underlineLinks}
              >
                {settings.underlineLinks ? 'ON' : 'OFF'}
              </button>
            </div>

            {/* Highlight Focus */}
            <div className="a11y-section">
              <label className="a11y-label">
                <Eye size={18} />
                Highlight Focus
              </label>
              <button
                onClick={() => updateSetting('highlightFocus', !settings.highlightFocus)}
                className={`a11y-toggle ${settings.highlightFocus ? 'active' : ''}`}
                aria-pressed={settings.highlightFocus}
              >
                {settings.highlightFocus ? 'ON' : 'OFF'}
              </button>
            </div>

            {/* Reduced Motion */}
            <div className="a11y-section">
              <label className="a11y-label">
                <RotateCcw size={18} />
                Reduce Motion
              </label>
              <button
                onClick={() => updateSetting('reducedMotion', !settings.reducedMotion)}
                className={`a11y-toggle ${settings.reducedMotion ? 'active' : ''}`}
                aria-pressed={settings.reducedMotion}
              >
                {settings.reducedMotion ? 'ON' : 'OFF'}
              </button>
            </div>
          </div>

          <div className="a11y-footer">
            <button onClick={resetSettings} className="a11y-reset">
              <RotateCcw size={16} />
              Reset All
            </button>
          </div>
        </div>
      )}

      {/* Backdrop */}
      {isOpen && (
        <div 
          className="a11y-backdrop"
          onClick={() => setIsOpen(false)}
          aria-hidden="true"
        />
      )}
    </>
  );
};

export default AccessibilityWidget;
